import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import ProductBottle from '../../../components/feature/ProductBottle';
import { useSEO, generateProductSchema, generateOrganizationSchema } from '../../../utils/seo';

const variants = [
  { id: '10mg', name: '10mg', size: '10mg vial', popular: true },
];

const relatedProducts = [
  {
    name: 'BPC-157',
    slug: '/product/bpc-157',
    image: 'https://public.readdy.ai/ai/img_res/1d0781ac7567d8a3c7f0b029cd82a713.jpg',
    specification: '10mg vial',
  },
  {
    name: 'GHK-Cu',
    slug: '/product/ghk-cu',
    image: 'https://readdy.ai/api/search-image?query=professional%20pharmaceutical%20clear%20glass%20vial%20with%20simple%20white%20rectangular%20label%20displaying%20only%20black%20text%20GHK-CU%20centered%20on%20clean%20dark%20charcoal%20gray%20studio%20background%20with%20soft%20diffused%20professional%20lighting%20minimalist%20modern%20premium%20biotech%20product%20photography%20empty%20transparent%20vial%20no%20powder%20no%20branding%20no%20logos%20no%20medical%20props%20clean%20neutral%20aesthetic&width=400&height=400&orientation=squarish&seq=ghkkpv',
    specification: '50mg vial',
  },
  {
    name: 'TB-500',
    slug: '/product/tb-500',
    image: 'https://readdy.ai/api/search-image?query=professional%20pharmaceutical%20clear%20glass%20vial%20with%20simple%20white%20rectangular%20label%20displaying%20only%20black%20text%20TB-500%20centered%20on%20clean%20dark%20charcoal%20gray%20studio%20background%20with%20soft%20diffused%20professional%20lighting%20minimalist%20modern%20premium%20biotech%20product%20photography%20empty%20transparent%20vial%20no%20powder%20no%20branding%20no%20logos%20no%20medical%20props%20clean%20neutral%20aesthetic&width=400&height=400&orientation=squarish&seq=tbkpv',
    specification: '5mg vial',
  },
];

export default function KPVPage() {
  const navigate = useNavigate();
  const [selectedVariant, setSelectedVariant] = useState(variants[0]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // SEO Configuration
  useSEO({
    title: 'KPV Research Peptide | COA-Backed Laboratory Supply | DN Biotech',
    description: 'KPV tripeptide for anti-inflammatory research. 99%+ purity, HPLC verified, COA included. Quote-based B2B laboratory supply with comprehensive documentation.',
    keywords: 'KPV research peptide, KPV COA, KPV laboratory supplier, anti-inflammatory peptide research, alpha-MSH peptides',
    canonical: `${siteUrl}/product/kpv`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(siteUrl),
        {
          '@type': 'WebPage',
          name: 'KPV Research Peptide - Laboratory Supply',
          description: 'Comprehensive information about KPV research peptide for laboratory use. COA-backed, HPLC verified, 99%+ purity.',
          url: `${siteUrl}/product/kpv`,
        }
      ]
    }
  });

  const handleRequestQuote = () => {
    navigate('/contact');
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/639178850200?text=I%20would%20like%20to%20request%20a%20quote%20for%20KPV', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Research Use Only Banner */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center gap-2 text-sm text-amber-800">
            <i className="ri-alert-line text-lg"></i>
            <span className="font-medium">For Research Use Only – Not for Human or Veterinary Use</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-12 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Product Image */}
            <div className="relative">
              <div className="sticky top-24">
                <ProductBottle productName="KPV" />
                
                {/* Trust Badges */}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                    <div className="text-2xl font-bold text-teal-600">99%+</div>
                    <div className="text-xs text-gray-600 mt-1">Purity</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                    <div className="text-sm font-bold text-teal-600">COA</div>
                    <div className="text-xs text-gray-600 mt-1">Included</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                    <div className="text-sm font-bold text-teal-600">HPLC</div>
                    <div className="text-xs text-gray-600 mt-1">Verified</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Product Info */}
            <div>
              <div className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-4">
                Recovery & Repair Research
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                KPV Research Peptide – COA-Backed Laboratory Supply
              </h1>
              
              <p className="text-lg text-gray-600 mb-8">
                Alpha-MSH-derived tripeptide for anti-inflammatory research. Research-grade KPV with comprehensive documentation, HPLC verification, and batch-specific COA for laboratory applications.
              </p>

              {/* Variant Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-900 mb-3">Specification:</label>
                <div className="grid grid-cols-1 gap-3">
                  {variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariant(variant)}
                      className="relative p-4 rounded-lg border-2 border-teal-600 bg-teal-50 transition-all"
                    >
                      <div className="font-semibold text-gray-900">{variant.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{variant.size}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-4 mb-8 p-6 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600">Purity</div>
                  <div className="font-semibold text-gray-900">≥99% (HPLC)</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Testing</div>
                  <div className="font-semibold text-gray-900">HPLC, MS</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Lead Time</div>
                  <div className="font-semibold text-gray-900">3-5 Business Days</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">MOQ</div>
                  <div className="font-semibold text-gray-900">1 Kit (10 Vials)</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleRequestQuote}
                  className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  Request Quote
                </button>
                <button
                  onClick={handleWhatsApp}
                  className="flex-1 px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold border-2 border-gray-300 hover:border-gray-400 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  WhatsApp Inquiry
                </button>
              </div>

              <p className="text-sm text-gray-500 mt-4 text-center">
                Quote-based pricing • Volume discounts available • Secure payment options
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is KPV */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What is KPV?</h2>
          <div className="prose prose-lg max-w-none text-gray-600">
            <p className="mb-4">
              KPV (Lys-Pro-Val) is a C-terminal tripeptide fragment derived from alpha-melanocyte-stimulating hormone (α-MSH), a naturally occurring peptide hormone. This short sequence has been identified as a key component responsible for certain anti-inflammatory properties observed in preclinical α-MSH research.
            </p>
            <p className="mb-4">
              In laboratory research contexts, KPV has been investigated for its potential anti-inflammatory mechanisms, immune modulation effects, and cellular signaling pathways in cellular and animal models. Research suggests it may interact with specific inflammatory mediators and cellular receptors.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 my-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Molecular Information</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Sequence:</span>
                  <span className="ml-2 text-gray-600">Lys-Pro-Val</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Molecular Weight:</span>
                  <span className="ml-2 text-gray-600">341.4 g/mol</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Formula:</span>
                  <span className="ml-2 text-gray-600">C₁₆H₃₁N₅O₄</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Source:</span>
                  <span className="ml-2 text-gray-600">α-MSH Fragment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Laboratory Research Applications */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Laboratory Research Applications</h2>
          <p className="text-gray-600 mb-8">
            KPV is utilized in various preclinical research contexts to investigate anti-inflammatory mechanisms and immune modulation:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-cross-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Inflammatory Response Research</h3>
              <p className="text-gray-600 text-sm">
                Investigation of inflammatory mediator regulation, cytokine production pathways, and inflammatory cascade modulation in laboratory models.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-microscope-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Immune Modulation Studies</h3>
              <p className="text-gray-600 text-sm">
                Research examining immune cell function, T-cell regulation, and immune response modulation in preclinical settings.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-heart-pulse-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Tissue Inflammation Research</h3>
              <p className="text-gray-600 text-sm">
                Studies investigating tissue-specific inflammatory responses, wound healing inflammation, and tissue repair processes in laboratory models.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-pulse-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cellular Signaling Studies</h3>
              <p className="text-gray-600 text-sm">
                Preclinical research examining melanocortin receptor interactions, intracellular signaling pathways, and cellular response mechanisms.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex gap-3">
              <i className="ri-alert-line text-xl text-amber-600 flex-shrink-0 mt-0.5"></i>
              <div>
                <h4 className="font-semibold text-amber-900 mb-2">Research Use Disclaimer</h4>
                <p className="text-sm text-amber-800">
                  KPV is intended exclusively for in vitro and in vivo laboratory research by qualified researchers. This product is not approved for human consumption, clinical applications, veterinary use, or any non-research purposes. All research must comply with institutional guidelines and applicable regulations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality & Verification */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Quality & Verification</h2>
          <p className="text-gray-600 mb-8">
            Every batch of KPV undergoes rigorous analytical testing to ensure research-grade quality and consistency:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Certificate of Analysis (COA)</h3>
                  <p className="text-sm text-gray-600">
                    Batch-specific COA provided with every order, documenting purity, identity confirmation, and quality control test results.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-chart-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">HPLC Analysis</h3>
                  <p className="text-sm text-gray-600">
                    High-Performance Liquid Chromatography confirms ≥99% purity and verifies peptide sequence integrity.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-microscope-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Mass Spectrometry</h3>
                  <p className="text-sm text-gray-600">
                    MS analysis confirms molecular weight and peptide identity, ensuring accurate composition.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Third-Party Testing</h3>
                  <p className="text-sm text-gray-600">
                    Independent laboratory verification available upon request for additional quality assurance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sample COA Preview */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Sample COA Preview</h3>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                ✓ Verified
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div>
                <div className="text-gray-600 mb-1">Purity (HPLC)</div>
                <div className="text-2xl font-bold text-gray-900">99.3%</div>
              </div>
              <div>
                <div className="text-gray-600 mb-1">Molecular Weight</div>
                <div className="text-2xl font-bold text-gray-900">341.4</div>
              </div>
              <div>
                <div className="text-gray-600 mb-1">Appearance</div>
                <div className="text-2xl font-bold text-gray-900">White Powder</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose DN Biotech */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose DN Biotech for KPV Supply</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">B2B Laboratory Focus</h3>
                  <p className="text-gray-600">
                    Dedicated to serving research institutions, universities, and biotech companies with professional-grade peptides and comprehensive documentation.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-text-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Documentation</h3>
                  <p className="text-gray-600">
                    Every order includes batch-specific COA, HPLC chromatograms, MS data, and handling guidelines for seamless laboratory integration.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-refresh-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Consistent Supply Chain</h3>
                  <p className="text-gray-600">
                    Reliable inventory management and fast turnaround times ensure your research timelines remain on track.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Technical Support</h3>
                  <p className="text-gray-600">
                    Our team provides expert guidance on product specifications, storage recommendations, and reconstitution protocols.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Order */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Order KPV</h2>
          <p className="text-gray-600 mb-8">
            Our quote-based ordering process ensures you receive accurate pricing and personalized service:
          </p>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Submit Inquiry</h3>
              <p className="text-sm text-gray-600">
                Request a quote via our contact form or WhatsApp with your specifications
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Receive Quote</h3>
              <p className="text-sm text-gray-600">
                Get detailed pricing, lead times, and product documentation within 24 hours
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Place Order</h3>
              <p className="text-sm text-gray-600">
                Confirm your order and complete secure payment via approved methods
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Fast Delivery</h3>
              <p className="text-sm text-gray-600">
                Receive your KPV with COA and documentation in 3-5 business days
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={handleRequestQuote}
              className="px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors text-lg whitespace-nowrap"
            >
              Request KPV Quote Now
            </button>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Research Peptides</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {relatedProducts.map((product) => (
              <a
                key={product.slug}
                href={product.slug}
                className="group bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
              >
                <div className="relative w-full aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                  <p className="text-sm text-gray-600">{product.specification}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Notice */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-8 border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Compliance & Research Use Notice</h3>
            <div className="prose prose-sm max-w-none text-gray-600">
              <p className="mb-3">
                <strong>For Research Use Only:</strong> KPV is intended exclusively for laboratory research by qualified professionals. This product is not approved for human consumption, clinical use, veterinary applications, or any non-research purposes.
              </p>
              <p className="mb-3">
                <strong>Regulatory Status:</strong> This peptide is not approved by the FDA, EMA, or other regulatory agencies for therapeutic use. It is supplied as a research reagent only.
              </p>
              <p>
                <strong>Buyer Responsibility:</strong> Purchasers must ensure compliance with all applicable local, state, and federal regulations governing the acquisition, storage, and use of research peptides.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
